import * as R from 'ramda'
import store from '@/store'
import { getTimeWithI18n } from '@Bill/utils'
import i18n from '@/locales'
import { getSuggestRuleConfig } from '@Bill/constants'
import { getCopyWithContentTableColumn } from '@/utils/common/tableColumn'

const getSetting = (row) => {
  const set = row.setting || row.rule_setting || {}
  return set.cloud_account_amount || (set.scale_rule ? set.scale_rule[0] : {}) || {}
}

export const getSettingTableColumn = () => {
  return {
    field: 'setting',
    title: i18n.t('bill.text_174'),
    minWidth: 70,
    showOverflow: 'title',
    slots: {
      default: ({ row }, h) => {
        return getSuggestRuleConfig({
          type: row.type,
          resource_type: row.res_type,
          time_from: row.time_from,
          ...(getSetting(row)),
        }).rule
      },
    },
    formatter: ({ row }) => {
      return getSuggestRuleConfig({
        type: row.type,
        resource_type: row.res_type,
        time_from: row.time_from,
        ...(getSetting(row)),
      }).rule
    },
  }
}

export const getSuggestRuleTableColumn = () => {
  return {
    field: 'suggest_rule',
    title: i18n.t('meter.suggestion_rule'),
    minWidth: 70,
    showOverflow: 'title',
    isResHide: true,
    formatter: ({ row }) => {
      return getSuggestRuleConfig({
        type: row.type,
        resource_type: row.res_type,
        time_from: row.time_from,
        adjust_ratio: row.adjust_ratio,
        ...(getSetting(row)),
      }).rule
    },
  }
}

export const getSuggestMethodTableColumn = () => {
  return {
    field: 'suggest_method',
    title: i18n.t('meter.accuracy_method'),
    minWidth: 70,
    showOverflow: 'title',
    isResHide: true,
    formatter: ({ row }) => {
      return getSuggestRuleConfig({
        type: row.type,
        resource_type: row.res_type,
        time_from: row.time_from,
        adjust_ratio: row.adjust_ratio,
        ...(getSetting(row)),
      }).method
    },
  }
}

export const getSuggestInfoTableColumn = () => {
  return {
    field: 'suggest_info',
    title: i18n.t('meter.suggestion_info'),
    minWidth: 70,
    showOverflow: 'title',
    isResHide: true,
    formatter: ({ row }) => {
      return getSuggestRuleConfig({
        type: row.type,
        resource_type: row.res_type,
        time_from: row.time_from,
        adjust_ratio: row.adjust_ratio,
        ...(getSetting(row)),
      }).info
    },
  }
}

export const getPeriodTableColumn = () => {
  return {
    field: 'period',
    title: i18n.t('bill.text_189'),
    minWidth: 70,
    showOverflow: 'title',
    isResHide: true,
    formatter: ({ row, cellValue }) => {
      return getTimeWithI18n(row.period)
    },
  }
}

export const getTimeFromTableColumn = () => {
  return {
    field: 'time_from',
    title: i18n.t('bill.text_198'),
    minWidth: 70,
    showOverflow: 'title',
    isResHide: true,
    slots: {
      header: ({ column }, h) => {
        return [
          <a-tooltip>
            <template slot="title">
              <div>{ i18n.t('bill.time_from_header_tips') }</div>
            </template>
            <span class='mr-1'>{ column.title }</span>
            <icon type="question" />
          </a-tooltip>,
        ]
      },
      default: ({ row }, h) => {
        let time = getTimeWithI18n(row.time_from)
        if (row.time_range) {
          time += `(${row.time_range})`
        }
        const ret = [
          <div class='text-truncate' title={ time }>
            { time }
          </div>,
        ]
        if (row.skip_holidays) {
          ret.push(<div class='text-truncate'>
            { i18n.t('meter.exclude_holidays') }
          </div>)
        }
        return ret
      },
    },
    formatter: ({ row }) => {
      let time = getTimeWithI18n(row.time_from)
      if (row.time_range) {
        time += `(${row.time_range})`
      }
      if (row.skip_holidays) {
        time += ` ${i18n.t('meter.exclude_holidays')}`
      }
      return time
    },
  }
}

export const getExecTimeTableColumn = ({ vm }) => {
  return {
    field: 'exec_time',
    title: i18n.t('bill.text_218'),
    minWidth: 70,
    showOverflow: 'title',
    isResHide: true,
    formatter: ({ row, cellValue }) => {
      if (!cellValue) {
        return '-'
      }
      return vm.$moment(cellValue).fromNow()
    },
  }
}

export const getPublicScopeTableColumn = ({
  field = 'public_scope',
  title = i18n.t('bill.apply_scope'),
  vm,
  resource,
  width = 110,
  hidden,
} = {}) => {
  return {
    title,
    field,
    showOverflow: 'title',
    width,
    isResHide: true,
    hidden: () => {
      if (!store.getters.l3PermissionEnable && (store.getters.scopeResource && store.getters.scopeResource.domain.includes(resource))) {
        return true
      }
      if (R.is(Function, hidden)) return hidden()
      return hidden
    },
    slots: {
      default: ({ row }, h) => {
        const i18nPrefix = store.getters.l3PermissionEnable ? 'suggestRuleShareDesc' : 'suggestRuleShareDescPrimary'
        if (row.is_public === false || row.is_public === 'false') return i18n.t(`${i18nPrefix}.none`)
        const { public_scope: publicScope, shared_projects: sharedProjects, shared_domains: sharedDomains } = row
        if (publicScope === 'project' && sharedProjects && sharedProjects.length > 0) {
          return [
            <a onClick={() => {
              vm.createDialog('CommonDialog', {
                hiddenCancel: true,
                header: i18n.t('bill.apply_scope'),
                body: () => {
                  return [
                    <a-alert class='mb-2' message={i18n.t('meter.rule_scope_resource', [sharedProjects.length, i18n.t('dictionary.project')])} />,
                    <dialog-table
                      vxeGridProps={{ showOverflow: 'title' }}
                      data={sharedProjects}
                      columns={
                        [
                          getCopyWithContentTableColumn({
                            field: 'id',
                            title: 'ID',
                            minWidth: 140,
                          }),
                          getCopyWithContentTableColumn({
                            field: 'name',
                            title: i18n.t('common_186'),
                          }),
                          getCopyWithContentTableColumn({
                            field: 'domain',
                            title: i18n.t('table.title.owner_domain'),
                          }),
                        ]
                      } />,
                  ]
                },
              })
            }}>{i18n.t(`${i18nPrefix}.project`)}</a>,
          ]
        }
        if (publicScope === 'domain') {
          if (sharedDomains && sharedDomains.length > 0) {
            return [
              <a onClick={() => {
                vm.createDialog('CommonDialog', {
                  hiddenCancel: true,
                  header: i18n.t('bill.apply_scope'),
                  body: () => {
                    return [
                      <a-alert class='mb-2' message={i18n.t('meter.rule_scope_resource', [sharedDomains.length, i18n.t('dictionary.domain')])} />,
                      <dialog-table
                        vxeGridProps={{ showOverflow: 'title' }}
                        data={sharedDomains}
                        columns={
                          [
                            getCopyWithContentTableColumn({
                              field: 'id',
                              title: 'ID',
                              minWidth: 140,
                            }),
                            getCopyWithContentTableColumn({
                              field: 'name',
                              title: i18n.t('common_186'),
                            }),
                          ]
                        } />,
                    ]
                  },
                })
              }}>{i18n.t(`${i18nPrefix}.domain`)}</a>,
            ]
          }
          return i18n.t(`${i18nPrefix}.projectAll`)
        }
        if (publicScope === 'system') {
          return i18n.t(`${i18nPrefix}.domainAll`)
        }
        return '-'
      },
    },
    formatter: ({ row }) => {
      const i18nPrefix = store.getters.l3PermissionEnable ? 'suggestRuleShareDesc' : 'suggestRuleShareDescPrimary'
      if (row.is_public === false || row.is_public === 'false') return i18n.t(`${i18nPrefix}.none`)
      const { public_scope: publicScope, shared_projects: sharedProjects, shared_domains: sharedDomains } = row
      if (publicScope === 'project' && sharedProjects && sharedProjects.length > 0) {
        return i18n.t(`${i18nPrefix}.project`)
      }
      if (publicScope === 'domain') {
        if (sharedDomains && sharedDomains.length > 0) {
          return i18n.t(`${i18nPrefix}.domain`)
        }
        return i18n.t(`${i18nPrefix}.projectAll`)
      }
      if (publicScope === 'system') {
        return i18n.t(`${i18nPrefix}.domainAll`)
      }
      return '-'
    },
  }
}

export const getTypeTableColumn = () => {
  return {
    field: 'type',
    title: i18n.t('bill.text_175'),
    minWidth: 70,
    showOverflow: 'title',
    formatter: ({ row }) => {
      return getSuggestRuleConfig({ type: row.type, resource_type: row.res_type, adjust_ratio: row.adjust_ratio }).method
    },
  }
}
